import { incrementTelemetry } from 'core-events/actions';
import React, { JSX, useContext, useEffect } from 'react';

import { NavSearchContext } from '../../context/NavSearchContext';
import { AdobePluginSuggestionVariant } from '../../types/shared';
import useMedia from '../../utils/useMedia';
import { ADOBE_PLUGIN_LANDING_PAGE_RELATIVE_PATH, ADOBE_PLUGIN_MARKETPLACE_URL } from './constants';

export interface AdobePluginSuggestionProps {
    searchTerm?: string;
    variant: AdobePluginSuggestionVariant;
    show: boolean;
    useNewSearchUI?: boolean;
    id?: string;
    selected?: boolean;
}

const ADOBE_KEYWORDS = ['after effects', 'aftereffects', 'premiere', 'premierepro', 'premiere pro', 'adobe'];

export const matchesAdobePluginKeywords = (searchTerm = ''): boolean => {
    const term = searchTerm.trim().toLowerCase();
    return ADOBE_KEYWORDS.some((keyword) => keyword.startsWith(term) || term.includes(keyword));
};

export const adobePluginCtaUrl = (variant: AdobePluginSuggestionVariant): string =>
    variant === AdobePluginSuggestionVariant.LANDING_PAGE
        ? ADOBE_PLUGIN_LANDING_PAGE_RELATIVE_PATH
        : ADOBE_PLUGIN_MARKETPLACE_URL;

export const useShowAdobePluginSuggestion = (searchTerm: string | undefined, show: boolean): boolean => {
    const isSmallScreen = useMedia('(max-width: 673px)', false);
    return !isSmallScreen && show && matchesAdobePluginKeywords(searchTerm);
};

const AdobePluginSuggestion = ({
    searchTerm = '',
    variant,
    show = false,
    useNewSearchUI = false,
    id,
    selected = false,
}: AdobePluginSuggestionProps): JSX.Element | false => {
    const context = useContext(NavSearchContext);

    const ctaText =
        variant === AdobePluginSuggestionVariant.LANDING_PAGE
            ? 'Learn more about the Storyblocks Plugin for Adobe Creative Cloud'
            : 'Try the Storyblocks Plugin For Adobe Creative Cloud';

    const ctaUrl = adobePluginCtaUrl(variant);

    const shouldShow = useShowAdobePluginSuggestion(searchTerm, show);

    useEffect(() => {
        if (shouldShow) {
            void incrementTelemetry('ppro.site-hook.typeahead-display');
        }
    }, []);

    return (
        shouldShow && (
            <div className="flex flex-nowrap items-center py-4 mt-4 border-t border-b-0 border-l-0 border-r-0 border-solid border-gray-300 font-semibold">
                <img
                    alt="After Effects Icon"
                    className={useNewSearchUI ? 'w-11 lg:w-6 mr-1' : 'w-6 mr-1'}
                    src={`${context.imagesUrl}/PremiereProPlugin/After-Effects-Icon-sm.png`}
                />
                <img
                    alt="Premiere Pro Icon"
                    className={useNewSearchUI ? 'w-11 lg:w-6 mr-1' : 'w-6 mr-1'}
                    src={`${context.imagesUrl}/PremiereProPlugin/Premiere-Pro-Icon-sm.png`}
                />
                <a
                    id={id}
                    tabIndex={-1}
                    className={`text-gray-950 ${useNewSearchUI ? 'w-44 lg:w-full mx-auto' : ''} ${
                        selected ? 'border-solid border-2 border-blue-500' : ''
                    }`}
                    href={ctaUrl}
                    target="_blank"
                    onClick={() => void incrementTelemetry('ppro.site-hook.typeahead-click')}
                    rel="noreferrer"
                >
                    {ctaText}
                </a>{' '}
                <div className={`ml-1 font-normal transform -rotate-45 ${useNewSearchUI ? 'mx-auto' : ''}`}>{'→'}</div>
            </div>
        )
    );
};

export default AdobePluginSuggestion;
